import { useLitterFormState } from '@application/state';
import {
  scrollToError,
  useConfirmModal,
  useModal,
  useRouter,
  useShowToast,
  useWindowConfirmUnload,
} from '@application/utils';
import { CountryCode } from '@gql/graphql';
import { ConfirmationModal, LoadingOverlay } from '@presentation/components';
import type { MtLitterPageFormValues } from '@presentation/pages';
import { useLog } from '@application/api';
import { MtLitterPage } from '@presentation/pages';
import type { FormMode } from '@presentation/types';
import dayjs from 'dayjs';
import { FormikProvider, useFormik } from 'formik';
import type { LocationDescriptor } from 'history';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCreateLitter } from './api/useCreateLitter';
import { useDeleteLitter } from './api/useDeleteLitter';
import { useGetAnimals } from './api/useGetAnimals';
import { useGetBreederBreeds } from './api/useGetBreederBreeds';
import { useGetLitter } from './api/useGetLitter';
import { useLitterInitialValuesBuilder } from './api/useLitterIntialValuesBuilder';
import { useUpdateLitter } from './api/useUpdateLitter';
import { useValidateForm } from './useValidateForm';

export const MtLitterPageController: FC = () => {
  // Persist form values
  // Override initial values
  // - Set when click on add parent
  // - Reset when new litter (click add new litter on MtLittersPage)
  // - Reset when new edit litter (click edit litter on MtLittersPage)
  const { setLitterFormState, litterFormState } = useLitterFormState();
  const { showToast } = useShowToast();

  const { infoFromCurrentPath, language, translate, navigateToRoute, routeLocationDescriptor } =
    useRouter();
  const { modal, show, close } = useModal();
  const {
    modal: bornModal,
    confirmedShow: confirmedShowBorn,
    confirmHandler: confirmHandlerBorn,
  } = useConfirmModal();
  const id = useMemo(() => {
    const idRaw = infoFromCurrentPath.params[0];
    if (!idRaw || idRaw === 'new') return;
    return Number(idRaw);
  }, [infoFromCurrentPath.params]);

  const { log } = useLog();

  const { litter, breederCountry, litterLoading } = useGetLitter(id);
  const { breedOptions, breedsLoading } = useGetBreederBreeds();
  const { animalOptionsByParentType, animals, animalsLoading } = useGetAnimals();
  const { litterInitialValuesBuilder } = useLitterInitialValuesBuilder();
  const { createLitterFromForm, createLitterLoading } = useCreateLitter();
  const { updateLitterFromForm } = useUpdateLitter(litter);
  const { deleteLitter, deleteLitterLoading } = useDeleteLitter();
  const { validate } = useValidateForm();

  const [modeState, setMode] = useState<FormMode>('view');
  const [showImageError, setShowImageError] = useState(false);

  const mode = useMemo(() => {
    if (litterFormState.mode === 'edit') return 'edit';
    if (!id || !litter) return 'new';
    if (litter.archived) return 'view';
    return modeState;
  }, [id, litter, modeState, litterFormState.mode]);

  const submitHandler = useCallback(
    async (values: MtLitterPageFormValues) => {
      if (mode === 'new') {
        await createLitterFromForm(values);
      } else {
        const totalDogs = (values.femalesCount ?? 0) + (values.malesCount ?? 0);
        // const wasBorn = litter?.born;
        if (values.born && !totalDogs) {
          showToast(translate('mt.breeder.litters.bornNoPuppiesWarning'), { type: 'error' });
          return;
        }
        await updateLitterFromForm(values);
      }

      navigateToRoute('mtLitters');
    },
    [createLitterFromForm, mode, navigateToRoute, showToast, translate, updateLitterFromForm],
  );

  const deleteConfirmHandler = useCallback(async () => {
    close();
    await deleteLitter(id);
    navigateToRoute('mtLitters');
  }, [close, deleteLitter, id, navigateToRoute]);

  const link = useMemo((): LocationDescriptor | undefined => {
    if (!litter?.adminValidated) return;
    const url = litter?.translations?.find((x) => x.language === language)?.url;
    if (url) {
      return routeLocationDescriptor('litter', { params: [url] });
    }
  }, [language, litter?.adminValidated, litter?.translations, routeLocationDescriptor]);

  // Enable / Disable the edition of parents / birthdate / availabilityDate
  const canEditSpecificFields: boolean = useMemo(() => {
    const today = dayjs().startOf('day');

    if (litter?.born) {
      return today.isBefore(dayjs(litter?.createdAt).endOf('day').add(30, 'days'));
    }
    return today.isBefore(dayjs(litter?.birthday).endOf('day').add(30, 'days'));
  }, [litter?.birthday, litter?.born, litter?.createdAt]);

  const form = useFormik<MtLitterPageFormValues>({
    initialValues: litterInitialValuesBuilder(mode !== 'new' ? litter : undefined),
    validate: (values) => validate(litter, values),
    enableReinitialize: true,
    onSubmit: submitHandler,
  });

  useEffect(() => {
    if (form.values?.FATHER) {
      const fatherId = form.values?.FATHER;
      const fatherPedigree = animals?.find((animal) => animal.id === fatherId)?.dogInfos?.pedigree;
      form.setFieldValue('fatherPedigree', fatherPedigree ?? undefined);
    } else {
      form.setFieldValue('fatherPedigree', undefined);
    }
    if (form.values?.MOTHER) {
      const motherId = form.values?.MOTHER;
      const motherPedigree = animals?.find((animal) => animal.id === motherId)?.dogInfos?.pedigree;
      form.setFieldValue('motherPedigree', motherPedigree ?? undefined);
    } else {
      form.setFieldValue('motherPedigree', undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animals, form.values?.FATHER, form.values?.MOTHER]);

  const onSubmitClickHandler = useCallback(async () => {
    if (form.errors) {
      log({ content: 'Mtlitterpage error: ', params: { errors: form.errors, litterId: id } });
    }
    await form.submitForm();

    scrollToError(form.errors);
    setShowImageError(true);
  }, [form, id, log]);

  useWindowConfirmUnload({ formIsDirty: form?.dirty, formIsSubmitting: form?.isSubmitting });

  return (
    <FormikProvider value={form}>
      <ConfirmationModal
        modal={bornModal}
        onConfirm={() => {
          confirmHandlerBorn();
          setMode('edit');
        }}
        text={translate('common.modals.confirmBorned.text')}
      />
      <ConfirmationModal
        modal={modal}
        onConfirm={deleteConfirmHandler}
        title={translate('common.modals.archiveLitter.title')}
        text={translate('common.modals.archiveLitter.text')}
      />
      <LoadingOverlay transparent isLoading={createLitterLoading || deleteLitterLoading} />
      <MtLitterPage
        loading={breedsLoading || litterLoading || animalsLoading}
        link={link}
        options={{
          breeds: breedOptions,
          animals: animalOptionsByParentType,
        }}
        adminValidated={litter?.adminValidated ?? undefined}
        archived={litter?.archived ?? undefined}
        mode={mode}
        onBackClick={() => navigateToRoute('mtLitters')}
        onEditClick={() => setMode('edit')}
        onCancelClick={() => setMode('view')}
        onDeleteClick={() => show()}
        onSubmitClick={onSubmitClickHandler}
        showImageError={showImageError}
        confirmBorn={confirmedShowBorn}
        onAddParentClick={async (parentType, formValues) => {
          if (formValues) {
            setLitterFormState({
              values: formValues,
              litterId: litter?.id ?? 'new',
              mode: litter?.id ? 'edit' : 'new',
            });
          }
          navigateToRoute('mtAnimals', { params: ['new'], search: { parentType } });
        }}
        canEditSpecificFields={canEditSpecificFields}
        showLitterCode={breederCountry === CountryCode.FR}
        born={litter?.born}
        log={log}
      />
    </FormikProvider>
  );
};
